import React, {
  MutableRefObject,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { useOnWheel } from "./hooks/useOnWheel";
import { useActiveCanvas } from "@/utils/utils";
import { useDispatch, useSelector } from "react-redux";
import {
  CursorType,
  setIsMiddleMousePressed,
} from "@/store/globalSettingsSlice";
import {
  useCanvasStore,
  isCanvasPosInitialized,
  useCanvasPosRealtime,
} from "@/store/zustand/canvasStore";
import useLayoutStore from "@/store/zustand/layoutStore";
import { useFullscreenToSize } from "./hooks/useFullscreenToSize";
import { makePassiveEventOption } from "@/utils/utils";
import { updateCanvasStoryboards } from "@/store/supabaseSlice";
import { useSupabaseStore } from "@/store/zustand/supabaseStore";

interface CanvasMovementOverlayProps {
  canvasRef: MutableRefObject<any>;
  iframesRef: MutableRefObject<any>;
  canvasContainerRef: MutableRefObject<HTMLDivElement>;
}

interface DragInfo {
  startX: number;
  startY: number;
  startTransformX: number;
  startTransformY: number;
}

/**
 * Handles movements like zoom, pan, space + drag to pan, etc
 */
export const CanvasMovementOverlay: React.FC<CanvasMovementOverlayProps> =
  React.memo(({ canvasRef, iframesRef, canvasContainerRef }) => {
    const isSpacePressed = useSelector(
      (state: any) => state.globalSettings.isSpacePressed
    );
    const isMiddleMousePressed = useSelector(
      (state: any) => state.globalSettings.isMiddleMousePressed
    );

    const cursorType = useSelector(
      (state: any) => state.globalSettings.cursorType
    );

    const canvasComponentKey = useCanvasStore(
      (state) => state.canvasComponentKey
    );

    const supabaseClient = useSupabaseStore((state) => state.supabaseClient);

    const dispatch = useDispatch<any>();

    const canvas = useActiveCanvas();

    const canvasPos = useCanvasPosRealtime();
    const canvasPosInitialized = isCanvasPosInitialized(canvasPos);

    const setCanvasPosValue = useCanvasStore(
      (store) => store.setCanvasPosValue
    );

    const fullscreenToSize = useFullscreenToSize();
    const canvasSize = useLayoutStore((state) => state.canvasSize);

    const onWheel = useOnWheel(canvasRef, iframesRef);

    const [scrollingToStoryboard, setScrollingToStoryboard] = useState(false);
    const [isDraggingForCursor, setIsDraggingForCursor] = useState(false);

    // Add these refs to track state across renders
    const isDraggingRef = React.useRef(false);
    const dragInfoRef = React.useRef<DragInfo | null>(null);
    const middleMouseButtonPointerIdRef = React.useRef<number | null>(null);

    useEffect(() => {
      const passive = makePassiveEventOption();
      passive["capture"] = false;

      // @ts-ignore
      canvasRef?.current?.addEventListener("wheel", onWheel, passive);

      return () => {
        // @ts-ignore
        canvasRef?.current?.removeEventListener("wheel", onWheel, passive);
      };
    }, [canvasRef]);

    /**
     * If the canvas position (transforms & scale) aren't set for a canvas,
     * set it on initial canvas load
     */
    useEffect(() => {
      // If the canvas panel isn't rendered don't apply
      if (!canvasSize) {
        return;
      }

      // No storyboards or canvas yet
      if (!canvas?.storyboards || !canvas.id) {
        return;
      }

      // Already has a canvas pos with all required properties
      if (canvasPosInitialized) {
        return;
      }

      // Otherwise, create a new one so you can fit the full width of the canvas
      const nonDeletedStoryboards = canvas.storyboards.filter(
        (storyboard) => !storyboard.deleted && !storyboard.preCreated
      );

      if (nonDeletedStoryboards.length === 0) {
        setCanvasPosValue(canvas.id, {
          zoomPerc: canvasComponentKey ? 2 : 1,
          transformX: 0,
          transformY: 0,
        });
        return;
      }

      const minX = Math.min(...nonDeletedStoryboards.map((s) => s.x));
      const maxX = Math.max(...nonDeletedStoryboards.map((s) => s.x + s.width));
      const minY = Math.min(...nonDeletedStoryboards.map((s) => s.y));
      const maxY = Math.max(
        ...nonDeletedStoryboards.map((s) => s.y + s.height)
      );

      fullscreenToSize(
        canvasContainerRef,
        maxX - minX,
        maxY - minY,
        minX,
        minY,
        {
          fillRatio: nonDeletedStoryboards.length === 1 ? 0.6 : 0.8,
          minScale: 2.5,
        }
      );
    }, [canvas, canvasSize, canvasPosInitialized]);

    /**
     * If there is a storyboard with a fitCanvasToStoryboard value, fit the canvas to that storyboard
     */
    useEffect(() => {
      // If the canvas panel isn't rendered don't apply
      if (!canvasSize || !canvas?.id || !supabaseClient) {
        return;
      }

      const storyboardToFit = canvas?.storyboards?.find(
        (storyboard) => storyboard.fitCanvasToStoryboard
      );

      if (!storyboardToFit) {
        return;
      }

      // Clear the fitCanvasToStoryboard value
      updateCanvasStoryboards(
        supabaseClient,
        dispatch,
        canvas.storyboards.map((storyboard) => {
          return {
            ...storyboard,
            fitCanvasToStoryboard: undefined,
          };
        }),
        canvas
      );

      fullscreenToSize(
        canvasContainerRef,
        storyboardToFit.width,
        storyboardToFit.height,
        storyboardToFit.x,
        storyboardToFit.y,
        {
          fillRatio: storyboardToFit.fitCanvasToStoryboard,
          minScale: 2.5,
        }
      );
    }, [canvas, canvasSize]);

    /**
     * If a new storyboard is created that needs to be scrolled to, start a process to scroll to that storyboard
     */
    useEffect(() => {
      const storyboardToScrollTo = canvas?.storyboards?.find(
        (storyboard) => storyboard.scrollToStoryboard
      );

      if (!storyboardToScrollTo || !canvas?.id) {
        return;
      }

      if (scrollingToStoryboard) {
        return;
      }

      if (!canvasPosInitialized) {
        return;
      }

      setScrollingToStoryboard(true);

      // Add a small delay to ensure everything is ready
      const interval = setTimeout(() => {
        // Remove the scrollToStoryboard trigger
        const newStoryboards = canvas.storyboards.map((storyboard) => {
          return {
            ...storyboard,
            scrollToStoryboard: false,
          };
        });

        const removeTrigger = async () => {
          if (!supabaseClient) {
            console.error("Supabase client not available");
            return;
          }
          await updateCanvasStoryboards(
            supabaseClient,
            dispatch,
            newStoryboards,
            canvas
          );
        };

        removeTrigger();

        const scrollToX =
          storyboardToScrollTo.x + storyboardToScrollTo.width / 2;
        const scrollToY =
          storyboardToScrollTo.y + storyboardToScrollTo.height / 2;

        // Animate to move to the transformX, transformY to scrollToX and scrollToY over time
        const startTime = Date.now();
        const duration = 500;
        const startTransformX = canvasPos.transformX;
        const startTransformY = canvasPos.transformY;

        const interval = setInterval(() => {
          const timeElapsed = Date.now() - startTime;
          const percentComplete = Math.min(1, timeElapsed / duration);

          const newTransformX =
            startTransformX + (scrollToX - startTransformX) * percentComplete;
          const newTransformY =
            startTransformY + (scrollToY - startTransformY) * percentComplete;

          setCanvasPosValue(canvas.id, {
            transformX: newTransformX,
            transformY: newTransformY,
          });

          if (percentComplete === 1) {
            clearInterval(interval);
            setScrollingToStoryboard(false);
          }
        }, 10);
      }, 50);

      return () => {
        clearInterval(interval);
        setScrollingToStoryboard(false);
      };
    }, [canvasPosInitialized, canvas?.storyboards]);

    const trackDragInfo = (e: PointerEvent) => {
      if (!canvasPosInitialized || !canvasPos) {
        return;
      }

      const newDragInfo: DragInfo = {
        startX: e.clientX,
        startY: e.clientY,
        startTransformX: canvasPos.transformX,
        startTransformY: canvasPos.transformY,
      };

      isDraggingRef.current = true;
      setIsDraggingForCursor(true);
      dragInfoRef.current = newDragInfo;
    };

    const clearDragInfo = () => {
      isDraggingRef.current = false;
      setIsDraggingForCursor(false);
      dragInfoRef.current = null;
    };

    useEffect(() => {
      const canvasContainer = canvasContainerRef.current;
      const eventTarget = document.documentElement;

      const handleMouseDown = (e: PointerEvent) => {
        const targetElement = e.target as HTMLElement;
        // Check if the click is on an element with data-mouse-down-id="true"
        const isClickOnToolbarButton = targetElement.closest(
          '[data-mouse-down-id="design-tool-button"]'
        );

        // Check if the pointer down is happening within the canvas container
        const isWithinCanvasContainer =
          canvasContainer?.contains(targetElement);

        // Check if the click is within the styling panel or its portals
        const isWithinStylingPanel =
          targetElement.closest('[data-mouse-down-id="styling-panel"]') || // Main styling panel
          targetElement.closest("#styling-panel-portal-root") || // Portal content (modals, dropdowns)
          document
            .getElementById("styling-panel-portal-root")
            ?.contains(targetElement); // Fallback check

        const panIntent =
          isSpacePressed || cursorType === CursorType.Pan || e.button === 1;

        // Only allow pan to start if within canvas container OR if it's a toolbar button,
        // but NOT if it's within the styling panel area
        if (
          panIntent &&
          ((!isWithinCanvasContainer && !isClickOnToolbarButton) ||
            isWithinStylingPanel)
        ) {
          return; // Don't process pan events outside canvas area or within styling panel
        }

        // If the intent is to pan, AND this click is NOT a primary click (e.button === 0)
        // on a toolbar button, then prevent default.
        // This allows primary clicks on toolbar buttons to proceed normally and change tools.
        if (panIntent && !(isClickOnToolbarButton && e.button === 0)) {
          e.preventDefault();
        }

        if (e.button === 1) {
          dispatch(setIsMiddleMousePressed(true));
          middleMouseButtonPointerIdRef.current = e.pointerId;
        }

        if (canvasPosInitialized) {
          trackDragInfo(e);

          // Capture pointer only if we are intending to pan and
          // NOT performing a primary click on a toolbar button.
          if (
            panIntent &&
            !(isClickOnToolbarButton && e.button === 0) &&
            isDraggingRef.current
          ) {
            try {
              eventTarget.setPointerCapture(e.pointerId);
            } catch (err) {
              console.warn(
                `Failed to set pointer capture for pointer ${e.pointerId} (button ${e.button}):`,
                err
              );
            }
          }
        }
      };

      const handleMouseUp = (e: PointerEvent) => {
        if (middleMouseButtonPointerIdRef.current === e.pointerId) {
          dispatch(setIsMiddleMousePressed(false));
          middleMouseButtonPointerIdRef.current = null;
        }

        if (isDraggingRef.current) {
          try {
            if (eventTarget.hasPointerCapture(e.pointerId)) {
              eventTarget.releasePointerCapture(e.pointerId);
            }
          } catch (err) {
            console.warn(
              `Error releasing pointer capture for ${e.pointerId} on mouseup:`,
              err
            );
          }
        }

        if (isDraggingRef.current) {
          clearDragInfo();
        }
      };

      const handleMouseMove = (e: PointerEvent) => {
        if (
          isMiddleMousePressed &&
          (e.buttons & 4) === 4 && // 4 is the bitmask for the middle mouse button ref: https://developer.mozilla.org/en-US/docs/Web/API/MouseEvent/buttons
          !dragInfoRef.current &&
          middleMouseButtonPointerIdRef.current !== null
        ) {
          trackDragInfo(e);

          // Update our reference to the current pointerId for the middle mouse
          middleMouseButtonPointerIdRef.current = e.pointerId;

          try {
            eventTarget.setPointerCapture(e.pointerId);
          } catch (err) {
            console.warn(
              `Failed to re-capture pointer ${e.pointerId} for middle mouse:`,
              err
            );
          }
        }

        if (
          isDraggingRef.current &&
          dragInfoRef.current &&
          canvasPosInitialized &&
          canvasPos &&
          canvas?.id &&
          (isSpacePressed ||
            cursorType === CursorType.Pan ||
            isMiddleMousePressed)
        ) {
          e.preventDefault(); // Prevent text selection and space input during drag

          // Calculate how far the mouse has moved
          const deltaX =
            (e.clientX - dragInfoRef.current.startX) /
            (canvasPos.zoomPerc * 0.45);
          const deltaY =
            (e.clientY - dragInfoRef.current.startY) /
            (canvasPos.zoomPerc * 0.45);

          // Adjust the position of the object
          setCanvasPosValue(canvas.id, {
            transformX: dragInfoRef.current.startTransformX - deltaX,
            transformY: dragInfoRef.current.startTransformY - deltaY,
          });
        }
      };

      // Attach to window to capture events outside browser
      const handleDocumentMouseLeave = (e: MouseEvent) => {
        if (isMiddleMousePressed) {
          if (middleMouseButtonPointerIdRef.current !== null) {
            try {
              if (
                eventTarget.hasPointerCapture(
                  middleMouseButtonPointerIdRef.current
                )
              ) {
                eventTarget.releasePointerCapture(
                  middleMouseButtonPointerIdRef.current
                );
              }
            } catch (err) {
              console.warn(
                "Error releasing middle mouse pointer capture on document mouseleave (for stored ID):",
                err
              );
            }
          }
          // dragInfoRef.current = null; // This is handled by clearDragInfo if called from mouseup
          // If mouse leaves document while middle mouse is held and potentially dragging, clear drag state.
          if (isDraggingRef.current) {
            clearDragInfo();
          }
        }
      };

      eventTarget.addEventListener("pointerdown", handleMouseDown);
      eventTarget.addEventListener("pointerup", handleMouseUp);
      eventTarget.addEventListener("pointermove", handleMouseMove);
      eventTarget.addEventListener("mouseleave", handleDocumentMouseLeave);

      return () => {
        eventTarget.removeEventListener("pointerdown", handleMouseDown);
        eventTarget.removeEventListener("pointerup", handleMouseUp);
        eventTarget.removeEventListener("pointermove", handleMouseMove);
        eventTarget.removeEventListener("mouseleave", handleDocumentMouseLeave);
      };
    }, [
      canvasPosInitialized,
      isSpacePressed,
      cursorType,
      isMiddleMousePressed,
      canvasPos?.zoomPerc,
      canvas?.id,
      setCanvasPosValue,
      dispatch,
    ]);

    // Update cursor to grabbing when actively dragging
    useEffect(() => {
      const docStyle = document.documentElement.style;

      if (isDraggingForCursor) {
        docStyle.setProperty("cursor", "grabbing", "important");
      } else {
        // Remove the override when not dragging to let Canvas component handle cursor
        docStyle.removeProperty("cursor");
      }
    }, [isDraggingForCursor]);

    return null;
  });
